import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DOCUMENTOS_LEGALES_PARSEADOS } from '../features/legal/documentos';

/**
 * `/legal` (P-01, respuestas del cliente 2026-09-17): índice de los cinco documentos legales.
 * Pública y sin guarda — igual que `/saber-mas` y `/roles`, sin `beforeLoad`: nadie debería tener
 * que iniciar sesión para leer una política de privacidad.
 *
 * También existe `routes/legal.$documento.tsx` (`/legal/$documento`): por la convención de rutas
 * planas de TanStack Router, un fichero cuyo nombre empieza por `legal.` anida automáticamente
 * bajo este (mismo mecanismo que `_autenticado.tsx` + `_autenticado/*.tsx`), así que esta ruta
 * hace doble papel de índice Y de layout con `<Outlet />` para el detalle — no hay
 * `routes/legal.index.tsx` en la lista de ficheros permitidos de esta tarea, así que el índice
 * vive en el propio `legal.tsx`.
 *
 * `legal:nav.volver` («Volver a legal», en `PaginaLegal`) solo tiene sentido si el índice y el
 * detalle son dos pantallas separadas, nunca visibles a la vez: por eso `useLocation` decide, y
 * la lista se pinta SOLO en `/legal` exacto, dejando paso a `<Outlet />` en cualquier ruta hija
 * — igual que `Layout` (`routes/__root.tsx`) ya decide qué pintar con `useMatches`/`staticData`
 * en vez de dejarlo fijo.
 */
export const Route = createFileRoute('/legal')({ component: PantallaLegal });

function PantallaLegal(): React.JSX.Element {
	const { t } = useTranslation('legal');
	const location = useLocation();

	if (location.pathname !== '/legal') {
		return <Outlet />;
	}

	return (
		<div>
			<h1 className='mb-6 font-black font-display text-2xl text-ink'>{t('nav.titulo')}</h1>
			<ul className='space-y-6'>
				{DOCUMENTOS_LEGALES_PARSEADOS.map(documento => (
					<li key={documento.slug}>
						<Link
							className='font-black text-ink underline hover:no-underline'
							params={{ documento: documento.slug }}
							to='/legal/$documento'
						>
							{t(`documentos.${documento.claveI18n}.titulo`)}
						</Link>
						<p className='text-ink text-sm'>{t(`documentos.${documento.claveI18n}.descripcion`)}</p>
						<p className='text-gray-ink text-xs'>{t('actualizadoEl', { fecha: documento.frontmatter.actualizado })}</p>
					</li>
				))}
			</ul>
		</div>
	);
}
